import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { CreditCard, Calendar, Users, AlertTriangle, ExternalLink } from 'lucide-react';
import { getUsageStats, getTeamMemberStats, SUBSCRIPTION_LIMITS } from '../../services/usageService';

const BillingManagement = ({ isInTrial: isInTrialProp, onShowPayments }) => {
  const { currentUser, currentOrg, subscription, trialInfo } = useAuth();
  const t = useTranslation();
  const [loading, setLoading] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [teamMemberStats, setTeamMemberStats] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);

  // Load usage statistics
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!currentOrg?.id) return;
      
      try {
        setLoadingUsage(true);
        const [searchStats, memberStats] = await Promise.all([
          getUsageStats(currentOrg.id),
          getTeamMemberStats(currentOrg.id)
        ]);
        setUsageStats(searchStats);
        setTeamMemberStats(memberStats);
      } catch (error) {
        console.error('Error loading usage stats:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    loadUsageStats();
  }, [currentOrg?.id]);

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      // Ensure currentUser is available
      if (!currentUser) {
        console.error('No user found');
        alert('Please log in to access payment portal');
        return;
      }

      // Get authentication token
      const token = await currentUser.getIdToken(true);
      
      // Cloud function URL
      const region = 'us-central1'; // or your specific region
      const projectId = 'sanctions-screener-20d59'; // your Firebase project ID
      const functionUrl = `https://${region}-${projectId}.cloudfunctions.net/createPortalSessionSimple`;
      
      // Make direct fetch call to the function
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          returnUrl: window.location.origin + '/org-admin'
        })
      });
      
      // Handle response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', response.status, errorData);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Open the portal URL in a new tab
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      // Show a meaningful error
      alert('Unable to access payment portal. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Use prop if provided, otherwise fall back to context
  const isInTrial = isInTrialProp !== undefined ? isInTrialProp : (trialInfo?.isInTrial || false);
  const trialDaysRemaining = trialInfo?.trialDaysRemaining || 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
          {t('orgAdmin.billing.title')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          {t('orgAdmin.billing.subtitle')}
        </p>
      </div>

      {/* Trial Status */}
      {isInTrial && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-blue-500 mr-3" />
              <div>
                <h3 className="font-medium text-blue-800 dark:text-blue-300">
                  {t('orgAdmin.billing.trialActive')}
                </h3>
                <p className="text-blue-700 dark:text-blue-400">
                  {trialDaysRemaining > 0 
                    ? t('orgAdmin.billing.trialDaysRemaining', { days: trialDaysRemaining })
                    : t('orgAdmin.billing.trialExpired')
                  }
                </p>
              </div>
            </div>
            {onShowPayments && (
              <button
                onClick={onShowPayments}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {t('orgAdmin.billing.upgradeNow')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Current Subscription */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4 flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          {t('orgAdmin.billing.currentSubscription')}
        </h3>

        {subscription ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('orgAdmin.billing.plan')}
                </label>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  {subscription.productName || t('orgAdmin.billing.unknownPlan')}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('orgAdmin.billing.status')}
                </label>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  subscription.status === 'active' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : subscription.status === 'past_due'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                  {subscription.status}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('orgAdmin.billing.nextBillingDate')}
                </label>
                <p className="text-slate-800 dark:text-slate-100">
                  {subscription.currentPeriodEnd 
                    ? new Date(subscription.currentPeriodEnd.seconds * 1000).toLocaleDateString()
                    : t('common.unknown')
                  }
                </p>
              </div>
            </div>

            {subscription.status === 'past_due' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
                  <div>
                    <h4 className="font-medium text-red-800 dark:text-red-300">
                      {t('orgAdmin.billing.paymentRequired')}
                    </h4>
                    <p className="text-red-700 dark:text-red-400">
                      {t('orgAdmin.billing.paymentOverdue')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
              {t('orgAdmin.billing.noActiveSubscription')}
            </h4>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('orgAdmin.billing.noSubscriptionMessage')}
            </p>
          </div>
        )}
      </div>

      {/* Organization Usage */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          {t('orgAdmin.billing.organizationUsage')}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {loadingUsage ? (
                <div className="animate-pulse bg-slate-300 dark:bg-slate-600 h-8 w-16 mx-auto rounded"></div>
              ) : teamMemberStats ? (
                <span className={teamMemberStats.isAtLimit 
                  ? 'text-red-600 dark:text-red-400' 
                  : teamMemberStats.isNearLimit 
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-slate-800 dark:text-slate-100'
                }>
                  {teamMemberStats.currentCount} / {teamMemberStats.limit}
                </span>
              ) : (
                currentOrg?.memberCount || 0
              )}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('orgAdmin.billing.activeMembers')}
            </div>
            {!loadingUsage && teamMemberStats && (
              <>
                <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      teamMemberStats.isAtLimit ? 'bg-red-500' :
                      teamMemberStats.isNearLimit ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ 
                      width: `${Math.min(100, (teamMemberStats.currentCount / teamMemberStats.limit) * 100)}%` 
                    }}
                  />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {teamMemberStats.remaining} remaining
                </div>
              </>
            )}
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {loadingUsage ? (
                <div className="animate-pulse bg-slate-300 dark:bg-slate-600 h-8 w-16 mx-auto rounded"></div>
              ) : (
                `${usageStats?.used || 0} / ${usageStats?.totalLimit || '∞'}`
              )}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('orgAdmin.billing.searchesThisMonth')}
            </div>
            {!loadingUsage && usageStats && (
              <>
                <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                                              !usageStats.allowed ? 'bg-red-500' :
                      usageStats.isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ 
                      width: `${Math.min(100, (usageStats.used / usageStats.totalLimit) * 100)}%` 
                    }}
                  />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {usageStats.remaining} remaining
                  {usageStats.additionalUsage > 0 && (
                    <span className="text-green-600 dark:text-green-400">
                      {' '}(+{usageStats.additionalUsage} bonus)
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {currentOrg?.caseCount || 0}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('orgAdmin.billing.activeCases')}
            </div>
          </div>
          
          <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {currentOrg?.apiCallCount || 0}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('orgAdmin.billing.apiCallsThisMonth')}
            </div>
          </div>
        </div>
      </div>

      {/* Billing Actions */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
          {t('orgAdmin.billing.billingActions')}
        </h3>
        
        <div className="space-y-3">
          <button
            onClick={handleManageSubscription}
            disabled={loading || isInTrial}
            className={`flex items-center justify-between w-full p-4 border border-slate-200 dark:border-slate-600 rounded-lg transition-colors ${
              isInTrial 
                ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-700' 
                : 'hover:bg-slate-50 dark:hover:bg-slate-700'
            } ${loading ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 text-slate-400 mr-3" />
              <div className="text-left">
                <div className="font-medium text-slate-800 dark:text-slate-100">
                  {loading ? t('orgAdmin.billing.opening') : t('orgAdmin.billing.manageSubscription')}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {isInTrial 
                    ? t('orgAdmin.billing.notAvailableDuringTrial')
                    : t('orgAdmin.billing.manageSubscriptionDesc')
                  }
                </div>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400" />
          </button>
          
          <button
            disabled
            className="flex items-center justify-between w-full p-4 border border-slate-200 dark:border-slate-600 rounded-lg opacity-50 cursor-not-allowed"
          >
            <div className="flex items-center">
              <Users className="w-5 h-5 text-slate-400 mr-3" />
              <div className="text-left">
                <div className="font-medium text-slate-800 dark:text-slate-100">
                  {t('orgAdmin.billing.usageAnalytics')}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {t('orgAdmin.billing.usageAnalyticsDesc')}
                </div>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Billing Information */}
      <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-2">
          {t('orgAdmin.billing.needHelp')}
        </h4>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          {t('orgAdmin.billing.needHelpMessage')}
        </p>
        <button className="text-blue-500 hover:text-blue-600 text-sm font-medium">
          {t('orgAdmin.billing.contactSupport')}
        </button>
      </div>
    </div>
  );
};

export default BillingManagement; 